/**
 * Patient chart deep-link path helpers.
 *
 * Canonical form: `/patients/:patientId/:section/:resourceId?`
 * Legacy form (optional): `/patients/:patientId/:resourceId/:section`
 *
 * Chart routes use a splat under `/patients/:patientId/*`; these helpers
 * parse/build that splat only.
 */

/** Flip to false after telemetry shows legacy mid-path links are gone. */
export const SUPPORT_LEGACY_PATIENT_CHART_RESOURCE_PATHS = true

/**
 * Known chart section dataKeys (must stay in sync with PatientChart.utils items).
 * Kept local to avoid a circular import through ListLayout.
 */
const CHART_SECTIONS = new Set([
  'clinical-summary',
  'risks-gaps',
  'demographics',
  'care-coordination-notes',
  'care-coordination-tasks',
  'timeline',
  'progress-notes',
  'order-tests',
  'orders',
  'labs-imaging',
  'documents',
  'consents',
  'vitals',
  'history-physical',
  'insurances',
  'diagnosis',
  'medications',
  'allergies',
  'immunizations',
  'procedures',
  'family-history',
  'social-history',
  'encounters',
  'med-equipment',
  'audit',
])

export function isChartSection(segment: string): boolean {
  return CHART_SECTIONS.has(segment)
}

export type PatientChartPath = {
  section: string
  resourceId?: string
  /** True when the splat matched the legacy `resourceId/section` shape. */
  isLegacy: boolean
}

/**
 * Parse the chart splat (`params['*']`) into section + optional resource id.
 * @param supportLegacy defaults to {@link SUPPORT_LEGACY_PATIENT_CHART_RESOURCE_PATHS}
 */
export function parsePatientChartSplat(
  splat: string | undefined,
  supportLegacy: boolean = SUPPORT_LEGACY_PATIENT_CHART_RESOURCE_PATHS,
): PatientChartPath {
  const parts = (splat ?? '').split('/').filter(Boolean)

  if (parts.length === 0) {
    return { section: 'demographics', isLegacy: false }
  }

  if (parts.length === 1) {
    return { section: parts[0], isLegacy: false }
  }

  const [first, second] = parts

  // Canonical: section/resourceId
  if (isChartSection(first)) {
    return { section: first, resourceId: second, isLegacy: false }
  }

  // Legacy: resourceId/section
  if (isChartSection(second)) {
    if (supportLegacy) {
      return { section: second, resourceId: first, isLegacy: true }
    }
    // Detected but ignored — surface as unknown section (first segment is an id)
    return { section: first, isLegacy: true }
  }

  // Unrecognized two-segment path: assume new form
  return { section: first, resourceId: second, isLegacy: false }
}

/** Absolute path for external navigations and deep links. */
export function buildPatientChartPath(
  patientId: string,
  section: string,
  resourceId?: string,
): string {
  if (resourceId) {
    return `/patients/${patientId}/${section}/${resourceId}`
  }
  return `/patients/${patientId}/${section}`
}

/** Relative path under `/patients/:patientId` for in-chart navigate/Navigate. */
export function buildPatientChartRelativePath(section: string, resourceId?: string): string {
  if (resourceId) {
    return `${section}/${resourceId}`
  }
  return section
}
